import { AppProps } from "next/app";
import { appWithTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import "../styles/index.css";
import "../styles/app-global.css";
import "../styles/scss/_color-schemes.scss";
import "../styles/scss/pages.scss";
import "../styles/scss/header.scss";
import "../styles/scss/main.scss";
import "../components/v2/tooltip/_style.scss";
import Head from "next/head";
import Script from "next/script";
import { mwImage } from "../lib/common";
import useLoadOnInteraction from "../hooks/useLoadOnInteraction";

const OG_LOCALE_MAP: Record<string, string> = {
  en: "en_US",
  es: "es_ES",
  ja: "ja_JP",
  ko: "ko_KR",
};

function MyApp({ Component, pageProps }: AppProps) {

  const [enableThirdParty, setEnableThirdParty] = useState(false);

  useLoadOnInteraction(() => {
    setEnableThirdParty(true);
  });

  useEffect(() => {
    requestAnimationFrame(() => {
      const hostname = window.location.hostname;
      if (
        hostname !== "middleware.io" &&
        hostname !== "localhost" &&
        hostname !== "wpstage.middleware.io"
      ) {
        window.location.href = "https://middleware.io/";
      }
      if (window.location.href === "https://middleware.io/sitemap.xml") {
        window.location.href = "https://middleware.io/sitemap-0.xml";
      }
    });

    if (window.top !== window.self) {
      document.write = (...text: string[]) => { };
      window.top.location.href = window.self.location.href;
      setTimeout(() => { document.body.innerHTML = ""; }, 1);
      window.self.onload = () => { document.body.innerHTML = ""; };
    }
  }, []);

  const home = {
    "@context": "https://schema.org/",
    "@type": "WebSite",
    name: "Middleware",
    url: "https://middleware.io/",
    alternateName: "Middleware",
  };

  const home_json = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    id: `https://middleware.io${pageProps?.uri}`,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home Page",
        item: {
          id: `https://middleware.io`,
          type: "WebPage",
        },
      },
    ],
  };
  const pages_json = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    id: `https://middleware.io${pageProps?.uri}`,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Page",
        item: {
          "@type": "WebPage",
          "@id": `https://middleware.io${pageProps?.uri}`,
        },
      },
    ],
  };
  const blogjson = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    id: `https://middleware.io${pageProps?.uri}`,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Blog",
        item: {
          id: `https://middleware.io/blog/`,
          type: "Thing",
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        name: pageProps?.pageTitle,
        item: {
          id: `https://middleware.io/${pageProps?.uri}`,
          type: "Thing",
        },
      },
    ],
  };
  let homeJson = JSON.stringify(home);
  let ldJson = JSON.stringify(home_json);
  let mainBlogJson = JSON.stringify(blogjson);
  let mainPageJson = JSON.stringify(pages_json);

  const isProd = process.env.NEXT_PUBLIC_VERCEL_ENV === "production";

  const robotsContent = isProd
    ? (pageProps?.metaRobots ?? "index, follow")
    : "noindex, nofollow";

  return (
    <>
      <Head>
        <title>
          {pageProps?.metaTitle ||
            "Middleware Observability Platform | Cloud-native Monitoring"}
        </title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width" />
        <link rel="canonical" href={`https://middleware.io${pageProps?.uri}`} />
        {/* {pageProps?.uri === "/" && (
          <link
            rel="preload"
            as="image"
            href="/images/Rethink-Observability-With-AI.webp"
            {...({ fetchpriority: "high" } as any)}
            type="image/webp"
          />
        )} */}
        <link rel="icon" href="/favicon/favicon.ico" type="image/x-icon" />
        <link
          rel="shortcut icon"
          href="/favicon/favicon.ico"
          type="image/x-icon"
        />
        {isProd && (
          <>
            <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
            <link
              rel="preconnect"
              href="https://www.googletagmanager.com"
              crossOrigin="anonymous"
            />
          </>
        )}
        <meta name="robots" content={robotsContent} />
        <meta name="title" content={pageProps?.metaTitle} />
        <meta name="description" content={pageProps?.description} />
        <meta name="msvalidate.01" content="6D37778B0B013AA1DF50936E77BCC811" />
        <meta name="og:site_name" content={"Middleware"} />
        <meta name="og:locale" content={"en_US"} />
        <meta
          property="og:url"
          content={`https://middleware.io${pageProps?.uri}`}
        />
        <meta name="twitter:card" content={"summary_large_image"} />
        <meta property="og:title" content={pageProps?.metaTitle} />
        <meta property="og:description" content={pageProps?.description} />
        <meta
          property="og:type"
          content={pageProps?.ogType ? pageProps?.ogType : "website"}
        />
        <meta
          property="og:image"
          content={
            pageProps?.ogImage != null
              ? mwImage(pageProps?.ogImage)
              : "https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png"
          }
        />
        <meta name="twitter:description" content={pageProps?.description} />
        <meta name="twitter:title" content={pageProps?.metaTitle} />
        <meta
          name="twitter:image"
          content={
            pageProps?.ogImage != null
              ? mwImage(pageProps?.ogImage)
              : "https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png"
          }
        />
        {pageProps?.faqSchema != null && !pageProps?.hasDynamicFAQ && (
          <script
            id="ld-json-faq"
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: pageProps.faqSchema }}
          />
        )}
      </Head>

      {/* RUM is first-party telemetry, so it is not gated behind
          `enableThirdParty` — otherwise bounced sessions are never recorded.
          `lazyOnload` defers it to the window load event + idle time, so it
          stays out of the LCP/TBT window Lighthouse scores. */}
      <Script
        id="middleware-rum"
        type={"text/javascript"}
        strategy="lazyOnload"
        src={
          "https://cdnjs.middleware.io/browser/libs/0.0.2/middleware-rum.min.js"
        }
        crossOrigin="anonymous"
        onLoad={() => {
          try {
            (window as any).Middleware?.track({
              serviceName: "Middleware website and docs",
              projectName: "Middleware website and docs",
              accountKey: "ojcwvgdtrejargoqitdvgywopkdqxjxjuxur",
              target: "https://p2i13hg.middleware.io",
              vcsCommitSHA: "6297167bf0d4ece1235154b4c3fdbefd2d5ef3c8",
              vcsRepositoryURL:
                "https://github.com/middleware-labs/mw-website-nextjs",
              tracePropagationTargets: [/middleware.io/i],
              defaultAttributes: {
                "app.version": `${process.env.APP_VERSION}_website`,
                source: "mw-website",
              },
            });
          } catch (e) {
            console.error("Error initializing Middleware RUM:", e);
          }
        }}
      />


        <Script strategy="afterInteractive" id="gtm-script">
          {`
(function(w,d,s,l,i){
  w[l]=w[l]||[];
  w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
  var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),
      dl=l!='dataLayer'?'&l='+l:'';
  j.async=true;
  j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
  f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-KKBGSWD');
          `}
        </Script>


      {enableThirdParty && (
        <Script strategy="lazyOnload" id="third-party-staggered">
          {`
    // Load Reddit after 1s
    setTimeout(function() {
      var s = document.createElement('script');
      s.src = 'https://embed.reddit.com/widgets.js';
      s.async = true;
      document.head.appendChild(s);
    }, 1000);

    // Load Twitter after 2s
    setTimeout(function() {
      var s = document.createElement('script');
      s.src = 'https://platform.twitter.com/widgets.js';
      s.async = true;
      document.head.appendChild(s);
    }, 2000);

    // Load Surface tag after 4s
    setTimeout(function() {
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/gh/trysurface/scripts@latest/surface_tag.min.js';
      s.dataset.siteId = 'cm3mbbq2b0002jt038o8fivyl';
      s.async = true;
      document.head.appendChild(s);
    }, 4000);

    // Load CookieYes after 5s
    setTimeout(function() {
      var s = document.createElement('script');
      s.src = 'https://cdn-cookieyes.com/client_data/1366a05e1d6c4c38f5f2bcff/script.js';
      s.async = true;
      document.head.appendChild(s);
    }, 5000);
    `}
        </Script>
      )}


      {enableThirdParty && (
        <Script type={"text/javascript"} strategy="lazyOnload" id="intercom-settings">
          {`
                    window.intercomSettings = {
                    api_base: "https://api-iam.intercom.io",
                    app_id: "zj0ibg91",
                    };
                `}
        </Script>
      )}

      {enableThirdParty && (
        <Script strategy="lazyOnload" id="intercom-widget">
          {`
        (function() {
          var w = window;
          var ic = w.Intercom;
          if (typeof ic === "function") {
            ic("reattach_activator");
            ic("update", w.intercomSettings);
          } else {
            var d = document;
            var i = function() {
              i.c(arguments);
            };
            i.q = [];
            i.c = function(args) {
              i.q.push(args);
            };
            w.Intercom = i;
            var l = function() {
              var s = d.createElement("script");
              s.type = "text/javascript";
              s.async = true;
              s.src = "https://widget.intercom.io/widget/zj0ibg91";
              var x = d.getElementsByTagName("script")[0];
              x.parentNode.insertBefore(s, x);
            };
            if (document.readyState === "complete") {
              l();
            } else if (w.attachEvent) {
              w.attachEvent("onload", l);
            } else {
              w.addEventListener("load", l, false);
            }
          }
        })();
      `}
        </Script>
      )}
      <Script
        type={"application/ld+json"}
        dangerouslySetInnerHTML={{ __html: `${homeJson}` }}
      ></Script>
      {pageProps?.uri !== "/" && pageProps?.uri !== "/blog" && (
        <Script
          type={"application/ld+json"}
          dangerouslySetInnerHTML={{ __html: `${mainPageJson}` }}
        ></Script>
      )}
      {pageProps?.uri == "/blog" && (
        <Script
          type={"application/ld+json"}
          dangerouslySetInnerHTML={{ __html: `${mainBlogJson}` }}
        ></Script>
      )}
      {pageProps?.uri == "/" && (
        <Script
          type={"application/ld+json"}
          dangerouslySetInnerHTML={{ __html: `${ldJson}` }}
        ></Script>
      )}
      {/*{pageProps?.uri  == '/blog' && <Script type={\"application/ld+json\"} dangerouslySetInnerHTML={{__html:`${ArticeldJson}`}} async></Script>}*/}


      <div>
        <Component {...pageProps} />
      </div>
    </>
  );
  // return <Component {...pageProps} />
}

export default appWithTranslation(MyApp);
